import analytics from "./analytics/analytics.js";
import { VoteType } from "./chatTypes.js";
import { localCache } from "./localMessagsCache.js";
import { browserStorageManager } from "./utils/browserStorage.js";
import pennyBroadcast, { BroadcastType } from "./utils/pennyBroadcast.js";
import { updatePennyDebugger } from "./utils/updatePennyDebugger.js";
const CHAT_URL = 'https://qaa.priceline.com/genai-svc/genai/chat/st/travelChat';
const JSON_START_DELIMITER = 'jsonstart';
const JSON_END_DELIMITER = 'jsonend';
const CHUNK_DELIMITER = '\r\r\n';
const fetchChatStream = async ({ submitChatMessagesPayload, url, headers, setStreamingText, handleJSON })=>{
    const pennysid = browserStorageManager.getBrowserStorage()?.getItem('pennysid') ?? '';
    const transformedSubmitChatMessagesPayload = {
        ...submitChatMessagesPayload,
        pennysid,
        prompts: [
            ...localCache.getAssistantRefPrompts(),
            ...submitChatMessagesPayload.prompts
        ]
    };
    try {
        let blob;
        const compressionEnabled = 'CompressionStream' in self;
        if (compressionEnabled) {
            const stream = new Blob([
                JSON.stringify(transformedSubmitChatMessagesPayload)
            ], {
                type: 'application/json'
            }).stream();
            const compressedReadableStream = stream.pipeThrough(new CompressionStream('gzip'));
            const compressedResponse = await new Response(compressedReadableStream);
            blob = await compressedResponse.blob();
        }
        const abortController = new AbortController();
        const timeoutId = setTimeout(()=>{
            abortController.abort();
        }, 30000);
        const res = await fetch(url || CHAT_URL, {
            method: 'POST',
            body: compressionEnabled ? blob : JSON.stringify(transformedSubmitChatMessagesPayload),
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                ...headers,
                'Content-Encoding': compressionEnabled ? 'gzip' : 'identity'
            },
            signal: abortController.signal
        });
        const timestamp = Date.now();
        if (submitChatMessagesPayload?.includeSpeech) pennyBroadcast({
            type: BroadcastType.NEW_MESSAGE_REQUEST_INITIATED,
            messageId: timestamp,
            sender: 'submitChatMessages.fetchChatStream'
        });
        clearTimeout(timeoutId);
        if (200 !== res.status) throw Error(`${res.status}: ${res.statusText}`);
        const pennysidHeader = res.headers.get('Pennysid');
        if (pennysidHeader) browserStorageManager.getBrowserStorage()?.setItem('pennysid', pennysidHeader);
        if (!res.body || !res.body.getReader) return {
            isOk: false,
            errorCode: 'NOT_SUPPORTED',
            errorMessage: 'ReadableStream not supported'
        };
        {
            const reader = res.body.getReader();
            let hasJson = false;
            let finalJson = '';
            let timer;
            while(true)try {
                const result = await reader.read();
                if (result?.done) {
                    if (timer) clearTimeout(timer);
                    setStreamingText('STREAMING_ENDED');
                    return {
                        isOk: true
                    };
                }
                if (result?.value) {
                    if (timer) clearTimeout(timer);
                    timer = setTimeout(()=>{
                        abortController.abort();
                    }, 20000);
                    const resultValue = result.value?.filter((x)=>0 !== x);
                    const response = new Response(resultValue);
                    const textRes = await response.text();
                    const textArray = textRes.split(CHUNK_DELIMITER);
                    while(textArray.length > 0){
                        const text = textArray.shift();
                        if (text === JSON_START_DELIMITER) hasJson = true;
                        else if (text === JSON_END_DELIMITER) {
                            hasJson = false;
                            handleJSON(finalJson);
                            finalJson = '';
                        } else if (hasJson) finalJson += text;
                        else {
                            setStreamingText(text);
                            if (submitChatMessagesPayload?.includeSpeech) pennyBroadcast({
                                type: BroadcastType.ADD_TEXT_TO_SPEECH_QUEUE,
                                data: text.replace(/\0/g, ''),
                                messageId: timestamp,
                                sender: 'submitChatMessages.fetchChatStream'
                            });
                        }
                    }
                }
            } catch (err) {
                return {
                    isOk: false,
                    errorCode: 'READER_READ_ERROR',
                    errorMessage: err?.message
                };
            }
        }
    } catch (err) {
        setStreamingText('STREAMING_ENDED');
        return {
            isOk: false,
            errorCode: 'ERROR',
            errorMessage: err?.message
        };
    }
};
const submitChatMessages = async ({ chatMessages, handleMarkerResponse, markerComponents, requestOptions: { url = '', headers = {} }, setChatMessages: setChatMessages, setErrorMessage, setIsDisablingInputValue, setLoadingStatus, setLoadingStatusMessage, setLoadingHotelSkeleton, setOpenErrorComponent, setStreamingText, submitChatMessagesPayload })=>{
    setLoadingStatus(true);
    setErrorMessage?.('');
    const fullTexts = [];
    const JSONResponses = [];
    const statusMessages = [];
    let containInlineMetadata = false;
    let hasHotelCardsMarker = false;
    analytics.log({
        message: 'Submit Chat Messages started'
    });
    const chatMsgs = [];
    const result = await fetchChatStream({
        submitChatMessagesPayload,
        url,
        headers,
        handleJSON: (jsonstring)=>{
            const parsedJson = JSON.parse(jsonstring);
            if (!containInlineMetadata && 'INLINE_METADATA_MARKER' === parsedJson.displayComponentName) containInlineMetadata = true;
            if (!hasHotelCardsMarker && 'HOTEL_CARDS_MARKER' === parsedJson.displayComponentName) {
                hasHotelCardsMarker = true;
                const componentMessage = createDefaultComponentMessage(parsedJson.displayComponentName, parsedJson.componentProps);
                chatMsgs.push(componentMessage);
                setLoadingHotelSkeleton?.(false);
                setChatMessages([
                    ...chatMessages,
                    componentMessage
                ]);
            }
            if (setLoadingStatusMessage && 'LOADING_STATUS_MESSAGE_MARKER' === parsedJson.displayComponentName) if (parsedJson.componentProps?.statusMessage === 'LOADING_SKELETON') setLoadingHotelSkeleton?.(true);
            else {
                statusMessages.push(parsedJson.componentProps?.statusMessage);
                setLoadingStatusMessage(statusMessages);
            }
            else JSONResponses.push(parsedJson);
        },
        setStreamingText: (text)=>{
            if ('STREAMING_ENDED' !== text) {
                fullTexts.push(text);
                const fullText = fullTexts.join('');
                setStreamingText?.(fullText);
            } else setStreamingText?.('');
        }
    });
    if (result?.isOk && 0 !== fullTexts.length) {
        const date = new Date();
        const finalTexts = fullTexts.join('').trim();
        if (finalTexts.toLowerCase().startsWith('system error')) setIsDisablingInputValue?.(true);
        if (finalTexts.length) {
            const promptMsg = {
                role: 'assistant',
                content: finalTexts
            };
            if (!containInlineMetadata && !hasHotelCardsMarker) {
                const chatMsg = {
                    ...promptMsg,
                    date,
                    vote: VoteType.NONE
                };
                chatMsgs.push(chatMsg);
            }
        }
        if (JSONResponses.length > 0) JSONResponses.forEach((jsonResponse)=>{
            const { displayComponentName, content: textContent, componentProps, assistantPromptMessage } = jsonResponse;
            if ('function' == typeof handleMarkerResponse && markerComponents?.[displayComponentName]) {
                const { role = 'assistant', content = displayComponentName, messageContent = '', markerComponentProps = {} } = handleMarkerResponse(void 0, displayComponentName, textContent, componentProps, chatMessages);
                const date = new Date();
                if (messageContent || content && content !== displayComponentName) {
                    const chatMessage = {
                        role,
                        content: messageContent || content,
                        date,
                        vote: VoteType.NONE
                    };
                    chatMsgs.push(chatMessage);
                }
                const chatMessage = {
                    role,
                    content: '',
                    displayComponentName,
                    markerComponentProps: {
                        ...componentProps,
                        ...markerComponentProps
                    },
                    date,
                    vote: VoteType.NONE
                };
                chatMsgs.push(chatMessage);
            } else {
                if ('HOTEL_CARDS_MARKER' !== displayComponentName || !hasHotelCardsMarker) chatMsgs.push(createDefaultComponentMessage(displayComponentName, componentProps));
                const shouldDisplayDelayFinalText = hasHotelCardsMarker && finalTexts.length > 0 && !containInlineMetadata;
                const shouldDisplayMarkerTextContent = textContent && textContent !== displayComponentName;
                if (shouldDisplayMarkerTextContent || shouldDisplayDelayFinalText) {
                    const chatMessage = {
                        role: 'assistant',
                        content: '',
                        date,
                        vote: VoteType.NONE
                    };
                    if (shouldDisplayDelayFinalText) {
                        chatMessage.content = finalTexts;
                        chatMsgs.push(chatMessage);
                    }
                    if (shouldDisplayMarkerTextContent) {
                        chatMessage.content = textContent;
                        chatMsgs.push(chatMessage);
                    }
                }
            }
            if (assistantPromptMessage) localCache.setAssistantRefPrompts(assistantPromptMessage);
        });
        updatePennyDebugger(submitChatMessagesPayload, [
            ...chatMessages,
            ...chatMsgs
        ], headers);
        const messagesWithNoStructuredData = chatMessages.filter((msg)=>'STRUCTURED_DATA' !== msg.displayComponentName);
        setChatMessages([
            ...messagesWithNoStructuredData,
            ...chatMsgs
        ]);
        setLoadingStatus(false);
        setLoadingStatusMessage([]);
        setLoadingHotelSkeleton?.(false);
    } else if (!result?.isOk) {
        if ('function' == typeof handleMarkerResponse) handleMarkerResponse(result, '');
        analytics.log({
            message: 'Failed to submit',
            ...result
        });
        setErrorMessage?.(result.errorMessage ?? 'Network error! Please check your network connection and retry.');
        setOpenErrorComponent(true);
        setLoadingStatus(false);
        setLoadingStatusMessage([]);
        setLoadingHotelSkeleton?.(false);
    }
};
function createDefaultComponentMessage(displayComponentName, componentProps) {
    return {
        role: '',
        content: '',
        displayComponentName,
        markerComponentProps: {
            ...componentProps
        },
        date: new Date(),
        vote: VoteType.NONE
    };
}
export { fetchChatStream, submitChatMessages };
